import { http } from '../http'
import type { Paginated } from '@/types/api'
import type {
  Label,
  LabelImportResult,
  LabelPayload,
  Language,
  LanguagePayload,
  ListLabelsParams,
  TranslationsResponse,
} from '@/types/translation'

/* ------------------------------- letture pubbliche ------------------------- */

/**
 * `GET /languages` — solo le lingue attive, pubblica.
 *
 * `silent`: gira all'avvio, e se l'API non risponde l'app ricade sulle lingue del
 * bundle. Un toast di rete qui non darebbe all'utente nulla su cui agire, e
 * arriverebbe comunque dalle altre chiamate di avvio.
 */
export function getLanguages() {
  return http
    .get<Language[]>('/languages', { meta: { silent: true } })
    .then((response) => response.data)
}

/** `GET /labels/translations/{iso}` — mappa piatta chiave→traduzione, pubblica. */
export function getTranslations(iso: string, options: { silent?: boolean } = {}) {
  return http
    .get<TranslationsResponse>(`/labels/translations/${iso}`, {
      meta: { silent: options.silent ?? false },
    })
    .then((response) => response.data)
}

/* --------------------------------- lingue ---------------------------------- */

/** `GET /languages/manage` — comprese le lingue non ancora pubblicate. */
export function listLanguages() {
  return http.get<Language[]>('/languages/manage').then((response) => response.data)
}

export function createLanguage(payload: LanguagePayload) {
  return http.post<Language>('/languages', payload).then((response) => response.data)
}

export function updateLanguage(id: number, payload: LanguagePayload) {
  return http.put<Language>(`/languages/${id}`, payload).then((response) => response.data)
}

/**
 * `DELETE /languages/{id}`.
 *
 * ⚠️ La FK delle traduzioni è in cascata: cancellare una lingua porta via tutto
 * il lavoro di traduzione fatto in quella lingua.
 */
export function deleteLanguage(id: number) {
  return http.delete<{ deleted: number }>(`/languages/${id}`).then((response) => response.data)
}

/* ---------------------------------- label ---------------------------------- */

/** `GET /labels/manage` — paginata, con le traduzioni di ogni lingua. */
export function listLabels(params: ListLabelsParams = {}) {
  return http.get<Paginated<Label>>('/labels/manage', { params }).then((response) => response.data)
}

export function createLabel(payload: LabelPayload) {
  return http.post<Label>('/labels', payload).then((response) => response.data)
}

export function updateLabel(id: number, payload: LabelPayload) {
  return http.put<Label>(`/labels/${id}`, payload).then((response) => response.data)
}

export function deleteLabel(id: number) {
  return http.delete<{ deleted: number }>(`/labels/${id}`).then((response) => response.data)
}

/** `DELETE /labels` — cancellazione multipla. */
export function deleteLabels(ids: number[]) {
  return http
    .delete<{ deleted: number }>('/labels', { data: { ids } })
    .then((response) => response.data)
}

/* ------------------------------ import / export ---------------------------- */

/**
 * `GET /labels/manage/export` — foglio xlsx con una colonna per lingua.
 *
 * ⚠️ Unica rotta di questo dominio che **non** risponde con l'envelope: arriva un
 * binario, quindi `responseType: 'blob'`. Di conseguenza anche un eventuale
 * errore arriva come blob e il suo `message` non è leggibile: `silent` evita un
 * toast con il messaggio grezzo di axios, e la view mostra un errore tradotto.
 */
export function exportLabels() {
  return http
    .get<Blob>('/labels/manage/export', {
      responseType: 'blob',
      meta: { silent: true },
      // Un foglio di qualche migliaio di righe non sta nei 20s di default.
      timeout: 120_000,
    })
    .then((response) => response.data)
}

/**
 * `POST /labels/manage/import` — multipart, campo `file`.
 *
 * L'header esplicito serve: questa istanza axios ha `application/json` come
 * default e `transformRequest` convertirebbe la FormData in JSON, lasciando il
 * file a terra (vedi `uploadPropic`). L'adapter poi riscrive l'header con il
 * boundary vero.
 */
export function importLabels(file: File) {
  const form = new FormData()
  form.append('file', file)

  return http
    .post<LabelImportResult>('/labels/manage/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000,
    })
    .then((response) => response.data)
}
